import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { MessageCircle, Building2, UserMinus } from "lucide-react";
import { toast } from "sonner";
import { t } from "@/i18n";
import { api } from "@/App";

export default function Connections() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [connections, setConnections] = useState([]);
  const [activeTab, setActiveTab] = useState("connections");
  const [proFollowing, setProFollowing] = useState([]);
  const [proLoading, setProLoading] = useState(true);
  const [unfollowTarget, setUnfollowTarget] = useState(null);
  
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const response = await api.get('/connections');
        setConnections(response.data.connections);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
      try {
        const r = await api.get('/me/following-professionals?limit=100');
        setProFollowing(r.data.following || []);
      } catch {
        setProFollowing([]);
      } finally {
        setProLoading(false);
      }
    };
    fetchAll();
  }, []);

  const handleUnfollow = async () => {
    if (!unfollowTarget) return;
    try {
      await api.delete(`/professional/${unfollowTarget.user_id}/follow`);
      setProFollowing((prev) => prev.filter((it) => it.professional?.user_id !== unfollowTarget.user_id));
      window.dispatchEvent(new Event("ade-following-changed"));
      toast.success("Collegamento ADE Pro rimosso");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setUnfollowTarget(null);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center bg-white">
        <div className="animate-pulse text-gray-300">...</div>
      </div>
    );
  }
  
  const getAvatarRingClass = (isVerified) => {
    return isVerified ? 'ring-2 ring-offset-1 ring-[#D4A574]' : '';
  };
  
  return (
    <div className="page-content bg-white" data-testid="connections-page">
      {/* Header */}
      <div className="px-4 pt-8 safe-top">
        <h1 className="text-lg font-medium text-gray-900">Collegamenti</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 px-4 mt-3">
        <button
          onClick={() => setActiveTab("connections")}
          className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
            activeTab === "connections" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-500"
          }`}
          data-testid="tab-connections"
        >
          Collegamenti
        </button>
        <button
          onClick={() => setActiveTab("pro")}
          className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
            activeTab === "pro" ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-500"
          }`}
          data-testid="tab-ade-pro"
        >
          <Building2 className="w-3.5 h-3.5" />
          ADE Pro
          {proFollowing.length > 0 && (
            <span className={`text-[10px] px-1.5 rounded-full ${activeTab === "pro" ? "bg-white/20" : "bg-gray-200"}`} data-testid="ade-pro-count">
              {proFollowing.length}
            </span>
          )}
        </button>
      </div>
      
      {/* Personal Connections List */}
      {activeTab === "connections" && (
      <div className="mt-4" data-testid="connections-list">
        {connections.length === 0 ? (
          <div className="px-4 py-16 text-center">
            <p className="text-sm text-gray-400">Nessun collegamento ancora.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {connections.map((conn) => (
              <div 
                key={conn.connection_id}
                className="flex items-center gap-3 px-4 py-3"
                data-testid={`connection-row-${conn.user.user_id}`}
              >
                <button
                  onClick={() => navigate(`/app/user/${conn.user.user_id}`)}
                  className="flex-shrink-0"
                >
                  <Avatar className={`w-11 h-11 ${getAvatarRingClass(conn.user.is_verified)}`}>
                    {conn.user.picture ? (
                      <AvatarImage src={conn.user.picture} alt={conn.user.name} />
                    ) : (
                      <AvatarFallback className="bg-gray-100 text-gray-400">
                        {conn.user.name?.charAt(0)?.toUpperCase()}
                      </AvatarFallback>
                    )}
                  </Avatar>
                </button>
                
                <button
                  onClick={() => navigate(`/app/user/${conn.user.user_id}`)}
                  className="flex-1 text-left min-w-0"
                >
                  <p className="text-sm font-medium text-gray-900 truncate">{conn.user.name}</p>
                  {conn.last_message && (
                    <p className="text-xs text-gray-400 truncate">
                      {conn.last_message.deleted_for_everyone
                        ? t("deletedTombstone")
                        : conn.last_message.encrypted
                          ? t("encryptedMessage")
                          : conn.last_message.content}
                    </p>
                  )}
                </button>
                
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => navigate(`/app/chat/${conn.user.user_id}`)}
                    className="p-2 rounded-full hover:bg-gray-100 transition-colors relative"
                  >
                    <MessageCircle className="w-4 h-4 text-gray-400" strokeWidth={1.5} />
                    {conn.unread_count > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-gray-900 text-white text-[10px] flex items-center justify-center">
                        {conn.unread_count}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* ADE Pro following list */}
      {activeTab === "pro" && (
      <div className="mt-4 px-4 space-y-2" data-testid="ade-pro-list">
        {proLoading ? (
          <div className="py-10 text-center text-gray-300 animate-pulse">...</div>
        ) : proFollowing.length === 0 ? (
          <div className="py-16 text-center" data-testid="ade-pro-empty">
            <p className="text-sm text-gray-400 mb-3">Non segui ancora nessun profilo ADE Pro.</p>
            <button
              onClick={() => navigate("/app/professionals")}
              className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold"
              data-testid="search-ade-pro-btn"
            >
              Cerca ADE Pro
            </button>
          </div>
        ) : (
          proFollowing.map((it) => {
            const p = it.professional || {};
            const pp = p.professional_profile || {};
            return (
              <div
                key={it.id}
                className="flex items-center gap-3 p-3 rounded-2xl border border-gray-100"
                data-testid={`ade-pro-item-${p.user_id}`}
              >
                <button onClick={() => navigate(`/app/pro/${p.user_id}`)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                  <img
                    src={p.picture || "/images/default-avatar.png"}
                    alt=""
                    className="w-12 h-12 rounded-xl object-cover bg-gray-100 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-semibold text-gray-900 truncate">{pp.professional_name || p.name}</p>
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-semibold text-blue-700 bg-blue-50 border border-blue-200">
                        ADE Pro
                      </span>
                    </div>
                    <p className="text-[11px] text-gray-500 truncate">
                      {p.username ? `@${p.username}` : ""}{pp.category ? `${p.username ? " · " : ""}${pp.category}` : ""}{pp.city ? ` · ${pp.city}` : ""}
                    </p>
                  </div>
                </button>
                <button
                  onClick={() => setUnfollowTarget(p)}
                  className="p-2 rounded-full hover:bg-red-50 transition-colors flex-shrink-0"
                  data-testid={`unfollow-pro-btn-${p.user_id}`}
                  aria-label="Rimuovi collegamento ADE Pro"
                >
                  <UserMinus className="w-4 h-4 text-gray-300 hover:text-red-400" strokeWidth={1.5} />
                </button>
              </div>
            );
          })
        )}
      </div>
      )}

      {/* Unfollow confirmation */}
      {unfollowTarget && (
        <div
          className="fixed inset-0 z-[100] bg-black/40 flex items-end"
          onClick={() => setUnfollowTarget(null)}
          data-testid="unfollow-overlay"
        >
          <div className="w-full bg-white rounded-t-3xl p-5 pb-8 safe-bottom" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-gray-900 mb-1">Rimuovi collegamento ADE Pro</h3>
            <p className="text-sm text-gray-500 mb-4">
              Non seguirai più <strong>{unfollowTarget.professional_profile?.professional_name || unfollowTarget.name}</strong>.
            </p>
            <button
              onClick={handleUnfollow}
              className="w-full py-3 rounded-xl bg-red-500 text-white text-sm font-semibold active:scale-[0.98] transition-transform mb-2"
              data-testid="confirm-unfollow-btn"
            >
              Smetti di seguire
            </button>
            <button
              onClick={() => setUnfollowTarget(null)}
              className="w-full py-3 rounded-xl text-gray-500 text-sm"
              data-testid="cancel-unfollow-btn"
            >
              Annulla
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
